import { useState } from "react";
import { Link } from "react-router";
import { ArrowLeft, Download } from "lucide-react";
import type { QuestionReport, SurveyReport } from "@/lib/surveys/report";
import type { ResponseRow } from "@/lib/surveys/types";
import { formatAdminDate } from "@/lib/format/admin-time";
import { StatusChip } from "./form";
import { adminFetch, adminFileUrl } from "@/api";

export interface Participants {
  rows: ResponseRow[];
  total: number;
  page: number;
  pageSize: number;
}

interface Props {
  surveyId: string;
  title: string;
  status: string;
  report: SurveyReport;
  participants: Participants;
}

const FACE_COLORS = ["#FCA5A5", "#FDBA74", "#FDE68A", "#BEF264", "#6EE7B7"];

function Bar({ label, count, pct, max }: { label: string; count: number; pct: number; max: number }) {
  return (
    <div className="grid grid-cols-[minmax(0,170px)_1fr_64px] items-center gap-3 text-[12.5px]">
      <span className="truncate text-zinc-300" title={label}>{label}</span>
      <div className="h-2.5 rounded-full bg-white/[0.05] overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-400" style={{ width: `${max ? (count / max) * 100 : 0}%` }} />
      </div>
      <span className="text-right text-zinc-400">{count} <span className="text-zinc-600">· {pct}%</span></span>
    </div>
  );
}

function QuestionBlock({ q }: { q: QuestionReport }) {
  return (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5 space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[13px] font-semibold text-white">Q{q.index + 1} · {q.text}</span>
        <span className="text-[11.5px] text-zinc-500 whitespace-nowrap">{q.answered} answered</span>
      </div>

      {q.kind === "choice" && (
        <div className="space-y-2">
          {[...q.rows].sort((a, b) => b.count - a.count).map((r) => (
            <Bar key={r.key} label={r.label} count={r.count} pct={r.pct} max={Math.max(...q.rows.map((x) => x.count), 1)} />
          ))}
          {q.otherTexts.length > 0 && (
            <details className="text-[12px] text-zinc-400">
              <summary className="cursor-pointer text-zinc-500">&quot;Other&quot; answers ({q.otherTexts.length})</summary>
              <ul className="mt-2 space-y-1">{q.otherTexts.map((t, i) => <li key={i}>• {t}</li>)}</ul>
            </details>
          )}
        </div>
      )}

      {q.kind === "rating" && (
        <div className="space-y-3">
          <div className="text-[24px] font-bold text-white">{q.average ?? "—"} <span className="text-[13px] text-zinc-500 font-normal">/ {q.scale} average</span></div>
          <div className="flex items-end gap-2 h-24">
            {q.distribution.map((d, i) => (
              <div key={d.value} className="flex-1 text-center text-[10.5px] text-zinc-500">
                <div
                  className="rounded-t-md mx-auto"
                  style={{ height: `${Math.max(d.pct, 2) * 0.7}px`, background: q.scale === 5 ? FACE_COLORS[i] : "#34D399" }}
                />
                <div className="mt-1">{d.pct}%</div>
                <div>{d.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {q.kind === "nps" && (
        <div className="space-y-2">
          <div className="text-[24px] font-bold text-white">{q.score == null ? "—" : q.score > 0 ? `+${q.score}` : q.score} <span className="text-[13px] text-zinc-500 font-normal">NPS</span></div>
          <div className="flex h-3 rounded-full overflow-hidden">
            <div style={{ width: `${q.detractors}%`, background: "#F87171" }} />
            <div style={{ width: `${q.passives}%`, background: "#FBBF24" }} />
            <div style={{ width: `${q.promoters}%`, background: "#34D399" }} />
          </div>
          <div className="flex gap-4 text-[11.5px] text-zinc-400">
            <span>Detractors (0–6) {q.detractors}%</span>
            <span>Passives (7–8) {q.passives}%</span>
            <span>Promoters (9–10) {q.promoters}%</span>
          </div>
        </div>
      )}

      {q.kind === "text" && (
        <ul className="space-y-2 max-h-64 overflow-y-auto">
          {q.answers.length === 0 && <li className="text-[12.5px] text-zinc-500">No answers yet.</li>}
          {q.answers.map((a, i) => (
            <li key={i} className="text-[12.5px] text-zinc-300 border-t border-white/[0.04] pt-2 first:border-0 first:pt-0">
              &ldquo;{a.text}&rdquo;
              {a.at && <span className="block text-[11px] text-zinc-600">{formatAdminDate(a.at)}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SurveyReportView({ surveyId, title, status, report, participants: initial }: Props) {
  const [participants, setParticipants] = useState(initial);
  const pages = Math.max(1, Math.ceil(participants.total / participants.pageSize));

  async function goTo(page: number) {
    const res = await adminFetch(`/api/surveys/${surveyId}/report?page=${page}`);
    if (res.ok) setParticipants((await res.json()).data.participants);
  }

  const k = report.kpis;
  const maxFunnel = Math.max(...report.funnel.map((f) => f.count), 1);

  return (
    <div className="space-y-5">
      <Link to={`/admin/surveys/${surveyId}`} className="inline-flex items-center gap-1 text-[12px] text-zinc-500 hover:text-zinc-300">
        <ArrowLeft className="w-3.5 h-3.5" /> {title}
      </Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-[22px] font-bold text-white tracking-tight">Report</h1>
        <StatusChip status={status} />
        <div className="flex-1" />
        <a
          href={adminFileUrl(`/api/surveys/${surveyId}/export`)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12.5px] font-semibold bg-emerald-500 text-black"
        >
          <Download className="w-3.5 h-3.5" /> Export Excel
        </a>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ["Completed", k.completed.toLocaleString("en-IN")],
          ["Completion rate", `${k.completionRate}%`],
          ["Avg rating", k.avgRating ?? "—"],
          ["NPS", k.nps == null ? "—" : k.nps > 0 ? `+${k.nps}` : String(k.nps)],
          ["Left feedback", k.leftFeedback.toLocaleString("en-IN")],
        ].map(([label, value]) => (
          <div key={label} className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-4">
            <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">{label}</div>
            <div className="text-[24px] font-bold text-white mt-1" data-testid={`kpi-${label}`}>{value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5 space-y-2.5">
        <span className="text-sm font-semibold text-white">Funnel</span>
        {report.funnel.map((f) => (
          <Bar key={f.label} label={f.label} count={f.count} pct={Math.round((f.count / maxFunnel) * 100)} max={maxFunnel} />
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {report.questions.map((q) => <QuestionBlock key={q.id} q={q} />)}
      </div>

      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
        <div className="px-5 py-3 text-sm font-semibold text-white border-b border-white/[0.06]">
          Participants <span className="text-zinc-500 font-normal">· {participants.total.toLocaleString("en-IN")}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                {["User", "Status", "Lang", "Version", "Shown", "Feedback", "Completed"].map((h) => (
                  <th key={h} className="px-5 py-2.5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {participants.rows.map((r) => (
                <tr key={r.userId} className="border-t border-white/[0.04]">
                  <td className="px-5 py-2.5 text-zinc-200">{r.email}</td>
                  <td className="px-5 py-2.5"><StatusChip status={r.status === "completed" ? "active" : r.status === "started" ? "paused" : "draft"} /> <span className="text-zinc-400 ml-1">{r.status}</span></td>
                  <td className="px-5 py-2.5 text-zinc-400 uppercase">{r.language ?? "—"}</td>
                  <td className="px-5 py-2.5 text-zinc-400">{[r.appVersion, r.platform].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="px-5 py-2.5 text-zinc-400">{r.shownCount}</td>
                  <td className="px-5 py-2.5 text-zinc-300 max-w-xs truncate" title={r.feedback ?? ""}>{r.feedback || "—"}</td>
                  <td className="px-5 py-2.5 text-zinc-500">{r.completedAt ? formatAdminDate(r.completedAt) : "—"}</td>
                </tr>
              ))}
              {participants.rows.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-6 text-center text-zinc-500">Nobody has seen this survey yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06] text-[12px] text-zinc-400">
            <button type="button" disabled={participants.page <= 1} onClick={() => goTo(participants.page - 1)} className="px-2.5 py-1 rounded-lg border border-white/[0.06] disabled:opacity-30">Prev</button>
            <span>Page {participants.page} of {pages}</span>
            <button type="button" disabled={participants.page >= pages} onClick={() => goTo(participants.page + 1)} className="px-2.5 py-1 rounded-lg border border-white/[0.06] disabled:opacity-30">Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
